import { Link } from "react-router-dom";
import { Compass, Mail, Phone, MapPin, MessageCircle, ArrowUpRight } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { CONTACT, SERVICE_GROUPS, SERVICES } from "@/data/site";
import { GUIDES } from "@/data/guides";
import { CITIES } from "@/data/cities";
import { api, formatApiErrorDetail } from "@/lib/api";

const svc = (slug) => SERVICES.find((s) => s.slug === slug);

export function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're subscribed. Portugal updates incoming.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-navy-deep text-warm relative overflow-hidden">
      <div className="spotlight absolute inset-x-0 top-0 h-64 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-20 relative">
        <div className="grid gap-14 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/60 bg-gold/10">
                <Compass className="h-5 w-5 text-gold" strokeWidth={1.5} />
              </span>
              <span className="font-playfair text-xl">Effizent <span className="text-gold">Europe</span></span>
            </div>
            <p className="mt-6 text-warm/60 max-w-sm leading-relaxed">
              Your Portugal Business & Relocation Partner. We don't just help you move to Portugal — we help you build your future in Portugal.
            </p>
            <form onSubmit={subscribe} className="mt-8 flex max-w-sm gap-2">
              <input
                data-testid="newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email for Portugal updates"
                className="flex-1 rounded-full bg-warm/5 border border-warm/15 px-5 py-3 text-sm text-warm placeholder:text-warm/40 focus:outline-none focus:border-gold/60"
              />
              <button data-testid="newsletter-submit" disabled={loading} className="rounded-full bg-gold px-5 py-3 text-sm font-600 text-navy hover:bg-gold-soft transition-colors disabled:opacity-60">
                {loading ? "…" : "Join"}
              </button>
            </form>
          </div>

          <FooterCol title="Build" links={SERVICE_GROUPS.BUILD.slugs.map((s) => ({ to: `/services/${s}`, label: svc(s).title }))} />
          <FooterCol title="Move & Grow" links={[...SERVICE_GROUPS.MOVE.slugs, ...SERVICE_GROUPS.GROW.slugs].slice(0, 7).map((s) => ({ to: `/services/${s}`, label: svc(s).title }))} />
          <div>
            <p className="accent-eyebrow text-gold mb-5">Learn</p>
            <ul className="space-y-3 text-sm text-warm/60">
              <li><Link to="/learn" className="hover:text-gold transition-colors">Knowledge Hub</Link></li>
              {GUIDES.slice(0, 3).map((g) => <li key={g.slug}><Link to={`/guides/${g.slug}`} className="hover:text-gold transition-colors">{g.title.split(":")[0]}</Link></li>)}
              {CITIES.slice(0, 2).map((c) => <li key={c.slug}><Link to={`/cities/${c.slug}`} className="hover:text-gold transition-colors">{c.name} Guide</Link></li>)}
            </ul>
          </div>
        </div>

        <div className="mt-16 grid gap-6 md:grid-cols-3 border-t border-warm/10 pt-10 text-sm text-warm/60">
          <a href={CONTACT.phoneHref} data-testid="footer-phone" className="flex items-center gap-3 hover:text-gold transition-colors"><Phone className="h-4 w-4 text-gold" /> {CONTACT.phone}</a>
          <a href={`mailto:${CONTACT.email}`} data-testid="footer-email" className="flex items-center gap-3 hover:text-gold transition-colors"><Mail className="h-4 w-4 text-gold" /> {CONTACT.email}</a>
          <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="footer-whatsapp" className="flex items-center gap-3 hover:text-gold transition-colors"><MessageCircle className="h-4 w-4 text-gold" /> WhatsApp us</a>
          <div className="md:col-span-3 grid gap-6 sm:grid-cols-2 pt-2">
            {CONTACT.offices.map((o) => (
              <div key={o.country} data-testid={`footer-office-${o.country.toLowerCase()}`} className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-gold shrink-0 mt-0.5" />
                <span><span className="text-warm/85 font-500">{o.country} Office</span><br />{o.address}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-warm/40">
          <p>© {new Date().getFullYear()} Effizent Europe. Portugal Business & Relocation Partner.</p>
          <Link to="/admin/login" data-testid="footer-admin-link" className="inline-flex items-center gap-1 hover:text-gold transition-colors">Admin <ArrowUpRight className="h-3 w-3" /></Link>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <p className="accent-eyebrow text-gold mb-5">{title}</p>
      <ul className="space-y-3 text-sm text-warm/60">
        {links.map((l) => (
          <li key={l.to}><Link to={l.to} className="hover:text-gold transition-colors">{l.label}</Link></li>
        ))}
      </ul>
    </div>
  );
}
